import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../../../redux/store";
import CheckedIcon from "../../../../../../../assets/svg/check-icon";
import EmptyCircleIcon from "../../../../../../../assets/svg/empty-circle-icon";
import { useGetTicketCategoriesQuery } from "../../../../../../../redux/services/helpdeskApi";
import type { TicketCategorySelectorProps } from "../utils/types";
import { getClientCRMFlag } from "../../../../../../../utils/constants/clientCustomCRMFlags";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";

const TicketCategorySelector = ({
  value,
  onChange,
}: TicketCategorySelectorProps) => {
  const helpdeskCategoryAccess = useSelector(
    (state: RootState) => state.auth.user?.helpdesk_category_access
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const { data: ticketCategories } = useGetTicketCategoriesQuery();

  const hasHelpdeskCategories = getClientCRMFlag("hasHelpdeskCategories");

  const categories = useMemo(() => {
    const allCategories = (ticketCategories ?? []).map((cat) => ({
      id: cat.key,
      label: cat.display_name,
    }));

    if (!hasHelpdeskCategories) return allCategories;

    if (!helpdeskCategoryAccess?.length) return allCategories;

    return allCategories;
  }, [ticketCategories, helpdeskCategoryAccess, hasHelpdeskCategories]);

  const selectedValue = value || categories[0]?.id || "";

  useEffect(() => {
    if (!value && selectedValue) {
      onChange(selectedValue);
    }
  }, [value, selectedValue, onChange]);

  const categoryCount = (categories?.length || 0) as number;

  const gridColsClass =
    categoryCount >= 3 ? "grid grid-cols-3" : "grid grid-cols-2";

  return (
    <div className="flex flex-col gap-2" data-form-field="ticketCategory">
      <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
        {helpdeskLabels.ticketCategory}
      </span>
      <div className={`${gridColsClass} gap-3`}>
        {categories?.map((category) => {
          const isSelected = selectedValue === category.id;
          return (
            <button
              key={category.id}
              type="button"
              onClick={() => onChange(category.id)}
              className={`flex max-h-[36px] min-w-0 items-center justify-center gap-1 p-3 rounded-[8px] border transition-colors cursor-pointer ${
                isSelected
                  ? "border-verba-blue dark:border-verba-blue-dk bg-applied-filters-tag-bg dark:bg-selected-option-highlight"
                  : "border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              }`}
            >
              {isSelected ? (
                <CheckedIcon className="w-[12px] h-[12px] text-verba-blue dark:text-verba-blue-dk shrink-0" />
              ) : (
                <EmptyCircleIcon className="w-[12px] h-[12px] text-gray-placeholder dark:text-gray-placeholder-dk shrink-0" />
              )}
              <TruncatedTextTooltip
                text={category?.label}
                maxWidth="max-w-full"
                className={`text-md font-semibold ${
                  isSelected
                    ? "text-verba-indigo dark:text-verba-indigo-dk"
                    : "text-heading-text dark:text-heading-text-dk"
                }`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TicketCategorySelector;
